import {useCallback, useMemo} from 'react';
import type {GridInitialState} from '@mui/x-data-grid-pro';
import configureStore from 'store/configureStore';
import {useAppDispatch, useAppSelector} from 'store/hooks';
import type {DataGridSettings} from '../types';
import {
  type DataGridSettingsOptions,
  mapDataGridSettings,
} from '../functions/mapDataGridSettings';
import {
  dataGridSettingsUpdated,
  selectSettingsDataGrid,
} from '../settingsSlice';

const defaultDataGridSettingsOptions: DataGridSettingsOptions = {
  storeSize: false,
  storePage: false,
  storeSorting: false,
  storeFilter: false,
};

export const useDataGridSettings = (
  name: string,
  options: Partial<DataGridSettingsOptions> = {},
): {
  initialState: DataGridSettings | undefined;
  saveState: (state: GridInitialState) => void;
} => {
  const dispatch = useAppDispatch();
  const storedSettings = useAppSelector(state =>
    selectSettingsDataGrid(state, name),
  );

  const {storeSize, storePage, storeSorting, storeFilter} = {
    ...defaultDataGridSettingsOptions,
    ...options,
  };

  // Restore only what this table stores, e.g. of converted legacy settings
  const initialState = useMemo(
    () =>
      storedSettings
        ? mapDataGridSettings(storedSettings, {
            storeSize,
            storePage,
            storeSorting,
            storeFilter,
          })
        : undefined,
    [storedSettings, storeFilter, storePage, storeSize, storeSorting],
  );

  const saveState = useCallback(
    (state: GridInitialState) => {
      dispatch(
        dataGridSettingsUpdated({
          name,
          settings: mapDataGridSettings(state, {
            storeSize,
            storePage,
            storeSorting,
            storeFilter,
          }),
        }),
      );
      // Write now, redux-persist otherwise writes on a timer that does not run on unload
      void configureStore.persistor.flush();
    },
    [dispatch, name, storeFilter, storePage, storeSize, storeSorting],
  );

  return {initialState, saveState};
};
